import { Box, Stack, Typography } from '@mui/material';
import { gridPivotActiveSelector } from '@mui/x-data-grid/internals';
import {
  getGridBooleanOperators,
  getGridMultiSelectOperators,
  getGridSingleSelectOperators,
  isAutogeneratedRow,
  type GridColDef,
  type GridFilterOperator,
} from '@mui/x-data-grid-premium';
import { CustomFieldType, type CustomField } from '../GeneratedClient';
import { v2Typography } from '../Families/v2Typography';
import { formatCustomFieldGridValue } from '../Generic/customFieldValue';
import type {
  ReferralAssignmentRoleV2,
  ReferralBrowserRowV2,
  ReferralCustomFieldValue,
} from './referralBrowserTypes';

type Option = { value: string; label: string };

const REFERRALS_CELL_MIN_HEIGHT = 48;
const maximumCategoricalReportingOptions = 50;
export const REFERRALS_SEARCH_FIELD = 'searchableText';

const statusValueOptions = [
  { label: 'Open', value: 'OPEN' },
  { label: 'Accepted', value: 'ACCEPTED' },
  { label: 'Closed', value: 'CLOSED' },
];

function displayValue(value: string | null | undefined) {
  return value || '-';
}

function formatDate(date: Date | null | undefined) {
  return date?.toLocaleDateString() ?? '';
}

function statusText(referral: ReferralBrowserRowV2) {
  const statusLabel =
    referral.status === 'OPEN'
      ? 'Open since'
      : referral.status === 'ACCEPTED'
        ? 'Accepted on'
        : 'Closed since';
  const statusDate =
    referral.status === 'OPEN'
      ? referral.openedAtUtc
      : referral.status === 'ACCEPTED'
        ? referral.acceptedAtUtc
        : referral.closedAtUtc;

  return [statusLabel, formatDate(statusDate)].filter(Boolean).join(' ');
}

function referralCommentPreview(comments: string) {
  return comments.length > 500 ? `${comments.slice(0, 500)}...` : comments;
}

function emptyOperators<
  V extends ReferralCustomFieldValue,
  F = V,
>(): GridFilterOperator<ReferralBrowserRowV2, V, F>[] {
  return [
    {
      label: 'is empty',
      value: 'isEmpty',
      requiresFilterValue: false,
      getApplyFilterFn: () => (value) =>
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0),
    },
    {
      label: 'is not empty',
      value: 'isNotEmpty',
      requiresFilterValue: false,
      getApplyFilterFn: () => (value) =>
        value !== null &&
        value !== '' &&
        (!Array.isArray(value) || value.length > 0),
    },
  ];
}

function formatCustomFieldValue(value: ReferralCustomFieldValue) {
  if (value === null) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return typeof value === 'string' ? value : '';
}

function arrayColumn(
  field: string,
  headerName: string,
  options: Option[],
  getValue: (row: ReferralBrowserRowV2) => string[] | null,
  getDisplayValue?: (row: ReferralBrowserRowV2) => string
): GridColDef<ReferralBrowserRowV2, string[] | null, string> {
  const labels = new Map(options.map((option) => [option.value, option.label]));
  const format = (values: string[] | null | undefined) =>
    (values ?? []).map((value) => labels.get(value) ?? value).join(', ');

  return {
    field,
    headerName,
    type: 'multiSelect',
    minWidth: 180,
    flex: 1,
    valueOptions: options,
    valueGetter: (_value, row) => getValue(row),
    valueFormatter: (value) => format(value),
    sortComparator: (first, second) =>
      format(first).localeCompare(format(second)),
    filterOperators: getGridMultiSelectOperators(),
    getApplyQuickFilterFn: () => null,
    renderCell: ({ row, value }) => (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          minHeight: REFERRALS_CELL_MIN_HEIGHT,
          minWidth: 0,
          width: '100%',
        }}
      >
        <Typography {...v2Typography.browserCell}>
          {getDisplayValue?.(row) || format(value) || '-'}
        </Typography>
      </Box>
    ),
  };
}

function assignmentColumns(
  assignmentRoles: ReferralAssignmentRoleV2[]
): GridColDef<ReferralBrowserRowV2>[] {
  return assignmentRoles.map(({ role, options }) => {
    const column = arrayColumn(
      `assignmentRole:${role}`,
      role,
      options,
      (row) => row.assignmentPersonIdsByRole[role] ?? [],
      (row) => row.assignmentNamesByRole[role]
    );
    return {
      ...column,
      filterOperators: getGridMultiSelectOperators().map((operator) =>
        operator.value === 'isEmpty'
          ? { ...operator, label: 'is unassigned' }
          : operator.value === 'isNotEmpty'
            ? { ...operator, label: 'is assigned' }
            : operator
      ),
    };
  });
}

export function buildReferralCustomFieldColumns(
  fields: CustomField[],
  rows: ReferralBrowserRowV2[]
): GridColDef<ReferralBrowserRowV2>[] {
  return fields.map((definition) => {
    const field = `referralCustomField:${definition.name}`;
    const getValue = (row: ReferralBrowserRowV2) =>
      row.customFieldValues[definition.name] ?? null;

    if (definition.type === CustomFieldType.StringArray) {
      const options = Array.from(
        new Set([
          ...(definition.validValues ?? []),
          ...rows.flatMap((row) => {
            const value = getValue(row);
            return Array.isArray(value) ? value : [];
          }),
        ])
      ).map((value) => ({ value, label: value }));
      return arrayColumn(field, definition.name, options, (row) => {
        const value = getValue(row);
        return Array.isArray(value) ? value : null;
      });
    }

    if (
      definition.type === CustomFieldType.DateOnly ||
      definition.type === CustomFieldType.DateTime
    ) {
      return {
        aggregable: false,
        chartable: false,
        field,
        headerName: definition.name,
        minWidth: 180,
        flex: 1,
        pivotable: false,
        type:
          definition.type === CustomFieldType.DateOnly ? 'date' : 'dateTime',
        valueGetter: (_value, row) => getValue(row),
        valueFormatter: (value) =>
          formatCustomFieldGridValue(definition.type!, value),
        getApplyQuickFilterFn: () => null,
        renderCell: ({ value }) => (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              minHeight: REFERRALS_CELL_MIN_HEIGHT,
              minWidth: 0,
              width: '100%',
            }}
          >
            <Typography {...v2Typography.browserCell}>
              {formatCustomFieldGridValue(definition.type!, value) || '-'}
            </Typography>
          </Box>
        ),
      };
    }

    if (
      definition.type === CustomFieldType.String &&
      definition.validValues?.length
    ) {
      const configuredValues = new Set(definition.validValues);
      const observedValues = rows.map(getValue);
      const categorical = observedValues.every(
        (value) =>
          value === null ||
          value === '' ||
          (typeof value === 'string' && configuredValues.has(value))
      );

      if (categorical) {
        return {
          field,
          headerName: definition.name,
          type: 'singleSelect',
          pivotable:
            configuredValues.size <= maximumCategoricalReportingOptions,
          chartable:
            configuredValues.size <= maximumCategoricalReportingOptions &&
            observedValues.every((value) => value !== null),
          minWidth: 180,
          flex: 1,
          valueOptions: definition.validValues,
          valueGetter: (_value, row) => getValue(row),
          valueFormatter: (value: string | null) => value ?? '',
          filterOperators: [
            ...getGridSingleSelectOperators(),
            ...emptyOperators<string | null>(),
          ],
          getApplyQuickFilterFn: () => null,
        };
      }
    }

    const isBoolean = definition.type === CustomFieldType.Boolean;
    return {
      field,
      headerName: definition.name,
      minWidth: 180,
      flex: 1,
      type: isBoolean ? 'boolean' : 'string',
      pivotable: isBoolean,
      chartable: isBoolean && rows.every((row) => getValue(row) !== null),
      ...(isBoolean
        ? {
            filterOperators: [
              ...getGridBooleanOperators(),
              ...emptyOperators<boolean | null>(),
            ],
          }
        : {}),
      valueGetter: (_value, row) => getValue(row),
      valueFormatter: (value: ReferralCustomFieldValue) =>
        formatCustomFieldValue(value),
      getApplyQuickFilterFn: () => null,
      renderCell: ({ value }) => (
        <Typography {...v2Typography.browserCell}>
          {formatCustomFieldValue(value) || '-'}
        </Typography>
      ),
    };
  });
}

export function buildReferralsColumns(
  rows: ReferralBrowserRowV2[],
  assignmentRoles: ReferralAssignmentRoleV2[],
  customFields: CustomField[],
  counties: string[],
  expanded: boolean
): GridColDef<ReferralBrowserRowV2>[] {
  const columns: GridColDef<ReferralBrowserRowV2>[] = [
    {
      field: 'referralCount',
      headerName: 'Referral count',
      type: 'number',
      width: 150,
      pivotable: true,
      chartable: true,
      aggregable: true,
      availableAggregationFunctions: ['sum'],
      getApplyQuickFilterFn: () => null,
    },
    {
      field: REFERRALS_SEARCH_FIELD,
      hideable: false,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      pinnable: false,
      getApplyQuickFilterFn: (value) => {
        const query = String(value ?? '')
          .trim()
          .toLowerCase();
        return (cellValue) =>
          String(cellValue ?? '')
            .toLowerCase()
            .includes(query);
      },
    },
    {
      field: 'title',
      flex: 1.3,
      headerName: 'Referral Title',
      minWidth: 220,
      getApplyQuickFilterFn: () => null,
      renderCell: ({ row }) => {
        const preview = referralCommentPreview(row.comments);
        return (
          <Stack
            spacing={0.75}
            sx={{
              justifyContent: 'center',
              minHeight: REFERRALS_CELL_MIN_HEIGHT,
              minWidth: 0,
              width: '100%',
            }}
          >
            <Typography {...v2Typography.browserCell}>
              {displayValue(row.title)}
            </Typography>
            {expanded && preview && (
              <Typography
                {...v2Typography.browserSecondary}
                color="text.secondary"
                sx={{ overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}
              >
                {preview}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'status',
      pivotable: true,
      chartable: true,
      headerName: 'Status',
      type: 'singleSelect',
      valueOptions: statusValueOptions,
      minWidth: 160,
      flex: 1,
      filterOperators: getGridSingleSelectOperators(),
      valueFormatter: (value: string | undefined, row, _column, apiRef) =>
        !row ||
        isAutogeneratedRow(row) ||
        (apiRef.current?.state && gridPivotActiveSelector(apiRef))
          ? (statusValueOptions.find((option) => option.value === value)
              ?.label ?? '')
          : statusText(row),
      getApplyQuickFilterFn: () => null,
      renderCell: ({ row }) => (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            minHeight: REFERRALS_CELL_MIN_HEIGHT,
            minWidth: 0,
            width: '100%',
          }}
        >
          <Typography {...v2Typography.browserCell} noWrap>
            {statusText(row)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'clientFamilyName',
      flex: 1,
      headerName: 'Client Family',
      minWidth: 180,
      getApplyQuickFilterFn: () => null,
      valueFormatter: (value: string | null) => value ?? '',
      renderCell: ({ value }) => (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            minHeight: REFERRALS_CELL_MIN_HEIGHT,
            minWidth: 0,
            width: '100%',
          }}
        >
          <Typography {...v2Typography.browserCell}>
            {displayValue(value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'county',
      pivotable: true,
      chartable: rows.every((row) => row.county !== null),
      headerName: 'County',
      type: 'singleSelect',
      valueOptions: counties,
      minWidth: 140,
      flex: 1,
      filterOperators: [
        ...getGridSingleSelectOperators(),
        ...emptyOperators<string | null>(),
      ],
      valueFormatter: (value: string | null) => value ?? '',
      getApplyQuickFilterFn: () => null,
      renderCell: ({ value }) => (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            minHeight: REFERRALS_CELL_MIN_HEIGHT,
            minWidth: 0,
            width: '100%',
          }}
        >
          <Typography {...v2Typography.browserCell}>
            {displayValue(value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'openedAtUtc',
      headerName: 'Date opened',
      type: 'dateTime',
      minWidth: 170,
      valueFormatter: (value: Date | null) => formatDate(value),
      getApplyQuickFilterFn: () => null,
    },
    {
      field: 'acceptedAtUtc',
      headerName: 'Date accepted',
      type: 'dateTime',
      minWidth: 170,
      valueFormatter: (value: Date | null) => formatDate(value),
      getApplyQuickFilterFn: () => null,
    },
    {
      field: 'closedAtUtc',
      headerName: 'Date closed',
      type: 'dateTime',
      minWidth: 170,
      valueFormatter: (value: Date | null) => formatDate(value),
      getApplyQuickFilterFn: () => null,
    },
    ...assignmentColumns(assignmentRoles),
    ...buildReferralCustomFieldColumns(customFields, rows),
  ];

  return columns.map((column) => ({
    groupable: false,
    aggregable: false,
    pivotable: false,
    chartable: false,
    availableAggregationFunctions: [],
    ...column,
    ...(column.valueGetter && {
      valueGetter: (value, row, definition, apiRef) =>
        isAutogeneratedRow(row)
          ? null
          : column.valueGetter!(value, row, definition, apiRef),
    }),
    ...(column.renderCell && {
      renderCell: (params) =>
        isAutogeneratedRow(params.row)
          ? typeof params.value === 'boolean'
            ? formatCustomFieldValue(params.value)
            : typeof params.value === 'string' ||
                typeof params.value === 'number'
              ? params.value
              : null
          : column.renderCell!(params),
    }),
  }));
}

export function isOptionalReferralsColumn(field: string) {
  return (
    ['referralCount', 'openedAtUtc', 'acceptedAtUtc', 'closedAtUtc'].includes(
      field
    ) || field.startsWith('referralCustomField:')
  );
}

export function isInternalReferralsColumn(field: string) {
  return field === REFERRALS_SEARCH_FIELD;
}
